import { useLayoutEffect, useRef } from "react";
import { useOverlayScrollbars } from "overlayscrollbars-react";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { FieldReference } from "@/components/organisms/Field/FieldReferences";
import { idToIdentifier } from "@/objects/@utils";
import { cn } from "@/components/ui/utils";
import {
  inferTypename,
  getTitleValue,
  formatDate,
  getObjectDef,
} from "./ToolCallCard.tools";
import type { NodeItem } from "./ToolCallCard.types";

/**
 * Render items as a table when there are multiple items.
 * Shows title field (from object definition) and createdAt.
 */
export function ToolCallCardTable({
  items,
  toolName,
}: {
  items: NodeItem[];
  toolName?: string | null;
}) {
  if (items.length === 0) return null;

  // Get typename and object definition
  const typename = inferTypename(items[0], toolName);
  const objectDef = getObjectDef(typename);
  const titleLabel = objectDef?.titleFieldKey
    ? objectDef.fieldDefinitions.find(
        (f) => f.objectKey === objectDef.titleFieldKey,
      )?.label || "Name"
    : "Name";
  const hasEntityColumn = Boolean(objectDef?.getEntityId);

  // Calculate if we need scrolling (more than 10 items)
  const maxVisibleRows = 10;
  const needsScroll = items.length > maxVisibleRows;

  // Overlay scrollbars for body
  const scrollRef = useRef<HTMLDivElement>(null);
  const [initialize] = useOverlayScrollbars({
    defer: true,
    options: {
      scrollbars: {
        visibility: "auto",
        autoHide: "scroll",
        autoHideDelay: 400,
      },
    },
  });

  useLayoutEffect(() => {
    if (scrollRef.current && needsScroll) {
      initialize(scrollRef.current);
    }
  }, [initialize, needsScroll]);

  return (
    <div className="isolate">
      {/* Fixed Header */}
      <table className="w-full border-collapse table-fixed">
        <thead>
          <tr>
            {hasEntityColumn && (
              <th className="p-[6px_8px] text-[11px] bg-muted [font-weight:600] uppercase text-muted-foreground/80 [border-bottom:1px_solid_var(--border)] w-9 min-w-9 text-left [border-right:1px_solid_var(--border)]" />
            )}
            <th className="p-[6px_8px] text-[11px] bg-muted [font-weight:600] uppercase text-muted-foreground/80 [border-bottom:1px_solid_var(--border)] w-[60%] text-left [border-right:1px_solid_var(--border)]">
              {titleLabel}
            </th>
            <th className="p-[6px_8px] text-[11px] bg-muted [font-weight:600] uppercase text-muted-foreground/80 [border-bottom:1px_solid_var(--border)] w-[40%] text-left">
              Created
            </th>
          </tr>
        </thead>
      </table>

      {/* Scrollable Body */}
      <div
        ref={scrollRef}
        className={cn(
          needsScroll && "max-h-82.5",
          needsScroll && "overflow-y-auto",
        )}
      >
        <table className="w-full border-collapse table-fixed">
          <tbody>
            {items.map((item, idx) => {
              const itemTypename = inferTypename(item, toolName);
              const itemObjectDef = getObjectDef(itemTypename);
              const titleValue = getTitleValue(item, itemObjectDef);
              const createdAt = item.created_at || item.createdAt;
              const base64Id = btoa(`${itemTypename}:${item.identifier}`);
              const isLastRow = idx === items.length - 1;
              // Get entity identifier: prefer entity.identifier (from tool output), else decode entity.id (GraphQL base64)
              const itemData = item as Record<string, unknown>;
              const entityObj = itemData?.entity as
                | { identifier?: string; id?: string }
                | undefined;
              const entityIdentifier =
                entityObj?.identifier ??
                (entityObj?.id && typeof entityObj.id === "string"
                  ? idToIdentifier(entityObj.id)
                  : null) ??
                (itemObjectDef?.getEntityId?.(itemData) &&
                typeof itemObjectDef.getEntityId(itemData) === "string"
                  ? idToIdentifier(itemObjectDef.getEntityId(itemData)!)
                  : null);

              return (
                <tr key={item.identifier || idx} className="hover:bg-muted">
                  {hasEntityColumn && (
                    <td
                      className={cn(
                        "p-[6px_8px] text-[13px] w-9 min-w-9 align-middle",
                        isLastRow
                          ? "[border-bottom-width:initial] [border-bottom-style:none] border-b-[initial]"
                          : "[border-bottom-width:1px] [border-bottom-style:solid] border-b-border",
                        "[border-right:1px_solid_var(--border)]",
                      )}
                    >
                      {entityIdentifier ? (
                        <BoxForEntity
                          identifier={entityIdentifier}
                          representation="character"
                          size="xs"
                        />
                      ) : (
                        "—"
                      )}
                    </td>
                  )}
                  <td
                    className={cn(
                      "p-[6px_8px] text-[13px] w-[60%]",
                      isLastRow
                        ? "[border-bottom-width:initial] [border-bottom-style:none] border-b-[initial]"
                        : "[border-bottom-width:1px] [border-bottom-style:solid] border-b-border",
                      "[border-right:1px_solid_var(--border)]",
                    )}
                  >
                    <FieldReference
                      value={{
                        __typename: itemTypename,
                        name: titleValue,
                        id: base64Id,
                        identifier: item.identifier || "",
                      }}
                      valueOptions={{
                        style: "related",
                        isNullable: false,
                      }}
                      isMultiline={true}
                      size="sm"
                    />
                  </td>
                  <td
                    className={cn(
                      "p-[6px_8px] text-[13px] w-[40%] text-muted-foreground/80",
                      isLastRow
                        ? "[border-bottom-width:initial] [border-bottom-style:none] border-b-[initial]"
                        : "[border-bottom-width:1px] [border-bottom-style:solid] border-b-border",
                    )}
                  >
                    {formatDate(createdAt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
